import { useQuery } from '@tanstack/react-query'
import { CalendarPlus, CheckCheck, PlayCircle, Save, Sparkles, Undo2, UsersRound } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { errorMessage } from '@/api/errors'
import { EmptyState, ErrorState, LoadingState } from '@/components/feedback/States'
import { toast } from '@/components/feedback/toast-store'
import { FormField } from '@/components/forms/FormField'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { PageHeader } from '@/components/ui/PageHeader'
import { Select } from '@/components/ui/Select'
import { classesQuery, yearsQuery } from '@/features/academic/api'
import { sectionLabel } from '@/features/academic/sectionLabel'
import type { AcademicYear, SchoolClass } from '@/features/academic/types'
import { useAuth } from '@/features/auth/useAuth'
import {
  boardQuery,
  capacityQuery,
  useApplyPlacements,
  useCancelPlacements,
  useConfirmPlacements,
  useSavePlacements,
  useUnconfirmPlacements,
  type BoardFilters,
} from '@/features/promotion/api'
import { PlacementTable } from '@/features/promotion/components/PlacementTable'
import { effectiveRow, needsPlace } from '@/features/promotion/rows'
import { ReenrollmentSettingsPanel } from '@/features/promotion/components/ReenrollmentSettingsPanel'
import { SeatSummaryPanel } from '@/features/promotion/components/SeatSummaryPanel'
import { DECISIONS, decisionLabel } from '@/features/promotion/labels'
import type { ApplyReport, BoardRow, DraftRow, PlacementDecision } from '@/features/promotion/types'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { cn } from '@/utils/cn'

type Tab = 'all' | 'to_decide' | 'requested' | 'not_returning' | 'confirmed'

function filtersFor(tab: Tab): Pick<BoardFilters, 'status' | 'requestStatus'> {
  switch (tab) {
    case 'to_decide':
      return { status: 'to_decide' }
    case 'confirmed':
      return { status: 'confirmed' }
    case 'requested':
      return { requestStatus: 'requested' }
    case 'not_returning':
      return { requestStatus: 'not_returning' }
    default:
      return {}
  }
}

function Board({ from, to, classes }: { from: AcademicYear; to: AcademicYear; classes: SchoolClass[] }) {
  const { t } = useTranslation('promotion')
  const { can } = useAuth()
  const canManage = can('students.manage')
  const [classId, setClassId] = useState<number | null>(null)
  const [sectionId, setSectionId] = useState<number | null>(null)
  const [examId, setExamId] = useState<number | null>(null)
  const [tab, setTab] = useState<Tab>('all')
  const [drafts, setDrafts] = useState<Record<number, DraftRow>>({})
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [bulk, setBulk] = useState<DraftRow>({
    decision: '',
    targetClassId: null,
    targetSectionId: null,
    note: null,
  })
  const [applyOpen, setApplyOpen] = useState(false)
  const [report, setReport] = useState<ApplyReport | null>(null)

  const filters: BoardFilters = {
    toYearId: to.id,
    fromYearId: from.id,
    classId,
    sectionId,
    examId,
    ...filtersFor(tab),
  }
  const board = useQuery(boardQuery(filters))
  const seats = useQuery(capacityQuery(to.id))
  const save = useSavePlacements()
  const confirm = useConfirmPlacements()
  const unconfirm = useUnconfirmPlacements()
  const cancel = useCancelPlacements()
  const apply = useApplyPlacements()

  const rows = useMemo(() => board.data?.rows ?? [], [board.data])
  const counts = board.data?.counts
  const dirtyCount = Object.keys(drafts).length
  const classFilter = classes.find((c) => c.id === classId)

  const fail = (e: unknown) => toast.error(t('The change could not be made'), errorMessage(e))

  const proposeFromResults = () => {
    const next = { ...drafts }
    let n = 0
    for (const row of rows) {
      if (row.placement || next[row.studentId] || !row.proposal) continue
      next[row.studentId] = {
        decision: row.proposal.decision,
        targetClassId: row.proposal.targetClass?.id ?? null,
        targetSectionId: row.proposal.targetSection?.id ?? null,
        note: null,
      }
      n++
    }
    setDrafts(next)
    if (n === 0)
      toast.info(t('No proposal to add'), t('Rows already planned or without a result are left as they are.'))
    else toast.success(t('{{count}} proposals added', { count: n }), t('Check them, then save.'))
  }

  const saveDrafts = () => {
    const payload = Object.entries(drafts)
      .filter(([, d]) => d.decision !== '')
      .map(([id, d]) => ({
        studentId: Number(id),
        decision: d.decision as PlacementDecision,
        targetClassId: needsPlace(d.decision) ? d.targetClassId : null,
        targetSectionId: needsPlace(d.decision) ? d.targetSectionId : null,
        note: d.note,
      }))
    if (payload.length === 0) {
      setDrafts({})
      return
    }
    save.mutate(
      { fromYearId: from.id, toYearId: to.id, rows: payload },
      {
        onSuccess: (r) => {
          setDrafts({})
          toast.success(t('{{count}} placements saved', { count: r.saved }))
        },
        onError: fail,
      },
    )
  }

  const selectedRows = rows.filter((r) => selected.has(r.studentId))
  const selectedPlacementIds = selectedRows
    .filter((r) => r.placement && !drafts[r.studentId])
    .map((r) => r.placement?.id ?? 0)

  const applyBulk = () => {
    if (bulk.decision === '') return
    const next = { ...drafts }
    for (const row of selectedRows) {
      if (row.placement?.status === 'applied') continue
      const place = needsPlace(bulk.decision)
      next[row.studentId] = {
        decision: bulk.decision,
        targetClassId: place
          ? (bulk.targetClassId ?? (bulk.decision === 'repeat' ? row.current.classId : null))
          : null,
        targetSectionId: place ? bulk.targetSectionId : null,
        note: effectiveRow(row, drafts[row.studentId]).note,
      }
    }
    setDrafts(next)
  }

  const confirmIds = (ids: number[]) => {
    if (dirtyCount > 0) {
      toast.warning(t('Save your changes first'), t('Unsaved rows cannot be confirmed.'))
      return
    }
    if (ids.length === 0) return
    confirm.mutate(ids, {
      onSuccess: (r) => {
        setSelected(new Set())
        toast.success(
          t('{{count}} placements confirmed', { count: r.confirmed }),
          t('Families are notified by email.'),
        )
      },
      onError: fail,
    })
  }
  const unconfirmIds = (ids: number[]) =>
    ids.length &&
    unconfirm.mutate(ids, {
      onSuccess: () => {
        setSelected(new Set())
        toast.success(t('Back to planned'))
      },
      onError: fail,
    })
  const cancelIds = (ids: number[]) =>
    cancel.mutate(ids, { onSuccess: () => toast.success(t('Placement cancelled')), onError: fail })

  const bulkClass = classes.find((c) => c.id === bulk.targetClassId)
  const tabs: { key: Tab; label: string; count?: number }[] = [
    { key: 'all', label: t('All'), count: counts?.total },
    { key: 'to_decide', label: t('To decide'), count: counts?.toDecide },
    { key: 'requested', label: t('Requested by the family'), count: counts?.requested },
    { key: 'not_returning', label: t('Not returning'), count: counts?.notReturning },
    { key: 'confirmed', label: t('Confirmed'), count: counts?.confirmed },
  ]

  return (
    <div className="space-y-6">
      {/* The table needs the full width below 2xl: the panels then sit above it, side by side. */}
      <div className="grid grid-cols-1 gap-6 2xl:grid-cols-[minmax(0,1fr)_22rem]">
        <Card className="min-w-0">
          <div className="flex flex-col gap-3 border-b border-line p-4 sm:flex-row sm:flex-wrap sm:items-end">
            <FormField label={t('Class')} className="sm:w-48">
              <Select
                value={classId ?? ''}
                placeholder={t('All classes')}
                options={classes.map((c) => ({ value: c.id, label: c.name }))}
                onChange={(e) => {
                  setClassId(e.target.value ? Number(e.target.value) : null)
                  setSectionId(null)
                }}
              />
            </FormField>
            <FormField label={t('Section')} className="sm:w-48">
              <Select
                value={sectionId ?? ''}
                disabled={!classFilter}
                placeholder={t('All sections')}
                options={(classFilter?.sections ?? []).map((s) => ({
                  value: s.id,
                  label: sectionLabel(classFilter?.name, s.name),
                }))}
                onChange={(e) => setSectionId(e.target.value ? Number(e.target.value) : null)}
              />
            </FormField>
            <FormField label={t('Results from')} className="sm:w-60">
              <Select
                value={examId ?? ''}
                placeholder={t('Latest published exam')}
                options={(board.data?.exams ?? []).map((x) => ({ value: x.id, label: x.name }))}
                onChange={(e) => setExamId(e.target.value ? Number(e.target.value) : null)}
              />
            </FormField>
          </div>
          <div
            role="tablist"
            aria-label={t('Filter')}
            className="flex gap-1 overflow-x-auto border-b border-line px-3 py-2"
          >
            {tabs.map((x) => (
              <button
                key={x.key}
                type="button"
                role="tab"
                aria-selected={tab === x.key}
                onClick={() => setTab(x.key)}
                className={cn(
                  'shrink-0 rounded-control px-3 py-1.5 text-sm font-medium',
                  tab === x.key ? 'bg-brand-50 text-brand-700' : 'text-ink-soft hover:bg-surface-muted',
                )}
              >
                {x.label}
                {x.count !== undefined ? <span className="ml-1.5 text-xs text-muted">{x.count}</span> : null}
              </button>
            ))}
          </div>

          {canManage ? (
            <div className="flex flex-wrap items-center gap-2 border-b border-line p-3">
              <Button variant="secondary" size="sm" leftIcon={<Sparkles />} onClick={proposeFromResults}>
                {t('Propose from results')}
              </Button>
              <Button
                size="sm"
                leftIcon={<Save />}
                disabled={dirtyCount === 0}
                loading={save.isPending}
                onClick={saveDrafts}
              >
                {dirtyCount > 0 ? t('Save ({{count}})', { count: dirtyCount }) : t('Save')}
              </Button>
              {dirtyCount > 0 ? (
                <Button variant="ghost" size="sm" onClick={() => setDrafts({})}>
                  {t('Discard changes')}
                </Button>
              ) : null}
            </div>
          ) : null}

          {canManage && selected.size > 0 ? (
            <div className="flex flex-col gap-2 border-b border-line bg-brand-50/40 p-3 sm:flex-row sm:flex-wrap sm:items-center">
              <span className="text-sm font-medium text-ink">
                {t('{{count}} selected', { count: selected.size })}
              </span>
              <div className="sm:w-40">
                <Select
                  size="sm"
                  aria-label={t('Decision for the selection')}
                  value={bulk.decision}
                  placeholder={t('Decision…')}
                  options={DECISIONS.map((d) => ({ value: d, label: decisionLabel(t, d) }))}
                  onChange={(e) =>
                    setBulk({
                      ...bulk,
                      decision: e.target.value as PlacementDecision | '',
                      targetClassId: null,
                      targetSectionId: null,
                    })
                  }
                />
              </div>
              <div className="sm:w-40">
                <Select
                  size="sm"
                  aria-label={t('Class for the selection')}
                  value={bulk.targetClassId ?? ''}
                  disabled={!needsPlace(bulk.decision)}
                  placeholder={t('Class…')}
                  options={classes.map((c) => ({ value: c.id, label: c.name }))}
                  onChange={(e) =>
                    setBulk({
                      ...bulk,
                      targetClassId: e.target.value ? Number(e.target.value) : null,
                      targetSectionId: null,
                    })
                  }
                />
              </div>
              <div className="sm:w-40">
                <Select
                  size="sm"
                  aria-label={t('Section for the selection')}
                  value={bulk.targetSectionId ?? ''}
                  disabled={!bulkClass}
                  placeholder={t('Section…')}
                  options={(bulkClass?.sections ?? []).map((s) => ({
                    value: s.id,
                    label: sectionLabel(bulkClass?.name, s.name),
                  }))}
                  onChange={(e) =>
                    setBulk({ ...bulk, targetSectionId: e.target.value ? Number(e.target.value) : null })
                  }
                />
              </div>
              <Button size="sm" variant="secondary" disabled={bulk.decision === ''} onClick={applyBulk}>
                {t('Set for the selection')}
              </Button>
              <div className="flex flex-wrap gap-2 sm:ml-auto">
                <Button
                  size="sm"
                  leftIcon={<CheckCheck />}
                  disabled={selectedPlacementIds.length === 0}
                  loading={confirm.isPending}
                  onClick={() => confirmIds(selectedPlacementIds)}
                >
                  {t('Confirm selected')}
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  leftIcon={<Undo2 />}
                  disabled={selectedPlacementIds.length === 0}
                  onClick={() => void unconfirmIds(selectedPlacementIds)}
                >
                  {t('Back to planned')}
                </Button>
              </div>
            </div>
          ) : null}

          {board.isPending ? (
            <LoadingState />
          ) : board.error ? (
            <ErrorState onRetry={() => void board.refetch()} />
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<UsersRound />}
              title={t('No student here')}
              description={t('Change the filters above.')}
            />
          ) : (
            <PlacementTable
              rows={rows}
              drafts={drafts}
              onChange={(id, value) => setDrafts((d) => ({ ...d, [id]: value }))}
              selected={selected}
              onToggle={(id) =>
                setSelected((s) => {
                  const n = new Set(s)
                  if (n.has(id)) n.delete(id)
                  else n.add(id)
                  return n
                })
              }
              onToggleAll={() =>
                setSelected((s) =>
                  rows.every((r) => s.has(r.studentId))
                    ? new Set()
                    : new Set(rows.map((r: BoardRow) => r.studentId)),
                )
              }
              classes={classes}
              seats={seats.data ?? []}
              canManage={canManage}
              onConfirm={(id) => confirmIds([id])}
              onUnconfirm={(id) => void unconfirmIds([id])}
              onCancel={(id) => cancelIds([id])}
            />
          )}
        </Card>

        <div className="order-first grid items-start gap-6 md:grid-cols-2 xl:grid-cols-3 2xl:order-none 2xl:block 2xl:space-y-6">
          <ReenrollmentSettingsPanel toYearId={to.id} canManage={canManage} />
          <SeatSummaryPanel seats={seats.data ?? []} />
          {canManage ? (
            <Card className="p-5">
              <p className="text-sm font-medium text-ink">{t('Start the new year')}</p>
              <p className="mt-1 text-xs text-muted">
                {t(
                  'Confirmed placements are applied automatically when {{year}} is set as the current year.',
                  {
                    year: to.name,
                  },
                )}
              </p>
              <Button
                className="mt-3"
                block
                variant="secondary"
                leftIcon={<PlayCircle />}
                onClick={() => setApplyOpen(true)}
              >
                {t('Apply confirmed placements')}
              </Button>
            </Card>
          ) : null}
        </div>
      </div>

      {report ? (
        <Alert
          tone={report.toDecide.length || report.skipped.length ? 'warning' : 'success'}
          title={t('Placements applied')}
        >
          <p>
            {t('{{count}} students moved to {{year}}.', {
              count: report.appliedTotal,
              year: report.toYear.name,
            })}
          </p>
          {report.toDecide.length ? (
            <p>
              {t('Still to decide (not applied): {{names}}', {
                names: report.toDecide.map((s) => s.fullName).join(', '),
              })}
            </p>
          ) : null}
          {report.withoutPlacement ? (
            <p>{t('{{count}} students have no placement yet.', { count: report.withoutPlacement })}</p>
          ) : null}
        </Alert>
      ) : null}

      <ConfirmDialog
        open={applyOpen}
        onOpenChange={setApplyOpen}
        tone="primary"
        title={t('Apply confirmed placements?')}
        description={t(
          'Each confirmed student is moved: the enrollment of {{from}} is closed and one is opened in the chosen section of {{to}}; leavers and graduates get their new status and lose portal access. Planned rows are not applied. This also happens automatically when {{to}} is set as the current year.',
          { from: from.name, to: to.name },
        )}
        confirmLabel={t('Apply')}
        loading={apply.isPending}
        onConfirm={() =>
          apply.mutate(to.id, {
            onSuccess: (r) => {
              setApplyOpen(false)
              setReport(r)
            },
            onError: (e) => {
              setApplyOpen(false)
              fail(e)
            },
          })
        }
      />
    </div>
  )
}

/** Admin: plan next year's classes (promotion, repetition, leavers, graduates) and re-enrolment. */
export function NextYearPage() {
  const { t } = useTranslation('promotion')
  useDocumentTitle('Next year')
  const years = useQuery(yearsQuery())
  const classes = useQuery(classesQuery())
  const [toYearId, setToYearId] = useState<number | null>(null)

  if (years.isPending || classes.isPending) return <LoadingState />
  if (years.error || classes.error)
    return (
      <ErrorState
        onRetry={() => {
          void years.refetch()
          void classes.refetch()
        }}
      />
    )

  const current = years.data.find((y) => y.isCurrent)
  const later = years.data
    .filter((y) => current && y.startsOn > current.startsOn)
    .sort((a, b) => a.startsOn.localeCompare(b.startsOn))
  const to = later.find((y) => y.id === toYearId) ?? later[0]

  const header = (
    <PageHeader
      title={t('Next year')}
      description={t(
        'Promotions, repeats, leavers and graduates, planned now and applied when the next year starts.',
      )}
      breadcrumbs={[{ label: t('Students'), to: '/admin/students' }, { label: t('Next year') }]}
      actions={
        later.length > 1 ? (
          <div className="w-44">
            <Select
              aria-label={t('Next academic year')}
              value={to?.id ?? ''}
              options={later.map((y) => ({ value: y.id, label: y.name }))}
              onChange={(e) => setToYearId(Number(e.target.value))}
            />
          </div>
        ) : null
      }
      meta={
        current && to ? <span className="text-sm text-muted">{`${current.name} → ${to.name}`}</span> : null
      }
    />
  )

  if (!current || !to) {
    return (
      <>
        {header}
        <Card>
          <EmptyState
            icon={<CalendarPlus />}
            title={
              current ? t('Create the next academic year first') : t('Set a current academic year first')
            }
            description={t(
              'Placements go from the current year to a later one. Create it (without making it current) in Academic years.',
            )}
            action={
              <Button asChild>
                <Link to="/admin/academic/years">{t('Open academic years')}</Link>
              </Button>
            }
          />
        </Card>
      </>
    )
  }

  return (
    <>
      {header}
      <Board key={to.id} from={current} to={to} classes={classes.data} />
    </>
  )
}
